import * as THREE from "three";
import type { CatalogItem, CatalogSection, Language } from "../types";
import { detectCompositeCover } from "../lib/coverLayout";
import { shelfFaceLoadPriority } from "../lib/shelfLoadPriority";

type Quality = "low" | "medium" | "high";
type StoreSceneOptions = {
  canvas: HTMLCanvasElement;
  onProductSelect(item: CatalogItem): void;
  onAgeConfirmed(): void;
  onProductUnavailable(itemId: string): void;
  onNavigationHover(label: string | null, clientX?: number, clientY?: number): void;
  onReady(): void;
  onError(): void;
};
type CollisionRect = { minX: number; maxX: number; minZ: number; maxZ: number };
type ShelfSlot = { mesh: THREE.Mesh; sectionIndex: number; itemIndex: number; surfaceKey: string };
type ShelfImageLoadTask = {
  priority: number;
  sequence: number;
  generation: number;
  run(): Promise<void>;
};
type NorenPanel = THREE.Mesh & { userData: { baseX: number; direction: number; index: number } };
type SignRecord = {
  front: THREE.Mesh;
  back: THREE.Mesh;
  ja: string;
  en: string;
  accent: string;
  key: string;
  aspect: number;
};
type NavigationZone = {
  mesh: THREE.Mesh<THREE.BufferGeometry, THREE.MeshBasicMaterial>;
  marker: NavigationMarker | null;
  destination: THREE.Vector3;
  label: Record<Language, string>;
};
type NavigationMarker = {
  group: THREE.Group;
  outerMaterial: THREE.MeshBasicMaterial;
  innerMaterial: THREE.MeshBasicMaterial;
  centerMaterial: THREE.MeshBasicMaterial;
};
type ProductHit = { item: CatalogItem; mesh: THREE.Mesh };

const PLAYER_HEIGHT = 1.78;
const PLAYER_RADIUS = 0.34;
const WALK_SPEED = 4.5;
const SHELF_IMAGE_LOAD_CONCURRENCY = 160;
const CEILING_Y = 4.08;
const STORE_BOUNDS = { minX: -7.55, maxX: 7.55, minZ: -8.55, maxZ: 15 };
const START_POSITION = new THREE.Vector3(0, PLAYER_HEIGHT, 14.4);
const NOREN_Z = 10.35;
// Stop the player shortly before the noren. Clicking the curtain begins the
// approach; the age dialog appears only after the short walk to this point.
const AGE_THRESHOLD_Z = NOREN_Z + 1.35;

// 防犯カメラの配置。x/z=設置位置、yaw=左右角度、pitch=上下角度（正で下向き）。ここを編集すれば自由に微調整できる。
const SECURITY_CAMERAS: ReadonlyArray<{ x: number; z: number; yaw: number; pitch: number }> = [
  { x: -4.0, z: 14.8, yaw: 2.10, pitch: 0.42 }, // 入り口付近（スポーン直前）
  { x: 7.1, z: 9.5, yaw: -2.85, pitch: 0.45 }, // 店内手前
  { x: -7.2, z: -8.2, yaw: 0.90, pitch: 0.55 }, // 店奥
];

// 天井からぶら下がる黒い店内スピーカーの配置。x/z=設置位置、rotationY=左右向き。
// 要素を増やせばスピーカーを追加できる。ここを編集すれば自由に微調整できる。
const HANGING_SPEAKERS: ReadonlyArray<{ x: number; z: number; rotationY: number }> = [
  { x: 7.8, z: -8.0, rotationY: -0.35 }, // 店奥右
];

const SECTION_LABELS = [
  { ja: "素人", en: "AMATEUR", accent: "#9b1727" },
  { ja: "単体作品", en: "SOLO TITLES", accent: "#194d85" },
  { ja: "独占配信", en: "EXCLUSIVE", accent: "#d6a90d" },
  { ja: "中出し", en: "CREAMPIE", accent: "#9b1727" },
  { ja: "巨乳", en: "BIG BUST", accent: "#194d85" },
  { ja: "4K", en: "4K", accent: "#d6a90d" },
  { ja: "美少女", en: "BEAUTIFUL GIRLS", accent: "#9b1727" },
  { ja: "人妻・主婦", en: "MARRIED WOMEN", accent: "#194d85" },
  { ja: "熟女", en: "MATURE", accent: "#d6a90d" },
  { ja: "ベスト・総集編", en: "BEST & COMPILATION", accent: "#9b1727" },
] as const;

export class StoreScene {
  private readonly canvas: HTMLCanvasElement;
  private readonly onProductSelect: (item: CatalogItem) => void;
  private readonly onAgeConfirmed: () => void;
  private readonly onProductUnavailable: (itemId: string) => void;
  private readonly onNavigationHover: (label: string | null, clientX?: number, clientY?: number) => void;
  private readonly onReady: () => void;
  private readonly onError: () => void;
  private readonly scene = new THREE.Scene();
  private readonly camera = new THREE.PerspectiveCamera(64, 1, 0.08, 70);
  private readonly clock = new THREE.Clock();
  private readonly raycaster = new THREE.Raycaster();
  private readonly pointer = new THREE.Vector2();
  private readonly productMeshes: THREE.Mesh[] = [];
  private readonly shelfSlots: ShelfSlot[] = [];
  private readonly norenPanels: NorenPanel[] = [];
  private readonly navigationZones: NavigationZone[] = [];
  private readonly signs: SignRecord[] = [];
  private readonly textureCache = new Map<string, THREE.Texture>();
  private readonly materialCache = new Map<string, THREE.Material>();
  private readonly materialLoads = new Map<string, Promise<THREE.Material | null>>();
  private readonly shelfImageLoadQueue: ShelfImageLoadTask[] = [];
  private readonly failedItemIds = new Set<string>();
  private readonly surfaceUsedIds = new Map<string, Set<string>>();
  private readonly animatedProductMeshes = new Set<THREE.Mesh>();
  private readonly collisions: CollisionRect[] = [];
  private readonly keys = new Set<string>();
  private renderer: THREE.WebGLRenderer | null = null;
  private floorMesh: THREE.Mesh | null = null;
  private currentSections: CatalogSection[] = [];
  private staffPicksPool: CatalogItem[] = [];
  private hoveredZone: NavigationZone | null = null;
  private hoveredProductMesh: THREE.Mesh | null = null;
  private frame = 0;
  private assignmentSerial = 0;
  private shelfImageLoadSequence = 0;
  private shelfImageLoadGeneration = 0;
  private activeShelfImageLoads = 0;
  private shelfImagePumpScheduled = false;
  private yaw = 0;
  private pitch = -0.015;
  private quality: Quality = "medium";
  private language: Language = "ja";
  private currentPointer: { id: number; x: number; y: number; moved: number } | null = null;
  private autoTarget: THREE.Vector3 | null = null;
  private ageConfirmed = false;
  private agePromptRequested = false;
  private floorGuidesHidden = false;
  private moveInput = new THREE.Vector2();
  private destroyed = false;
  private readonly isTouch = typeof window !== "undefined" && window.matchMedia("(pointer: coarse), (max-width: 699px)").matches;
  private mobileHighlightedZones = new Set<NavigationZone>();

  constructor(options: StoreSceneOptions) {
    this.canvas = options.canvas;
    this.onProductSelect = options.onProductSelect;
    this.onAgeConfirmed = options.onAgeConfirmed;
    this.onProductUnavailable = options.onProductUnavailable;
    this.onNavigationHover = options.onNavigationHover;
    this.onReady = options.onReady;
    this.onError = options.onError;
  }

  async start(): Promise<void> {
    try {
      this.renderer = new THREE.WebGLRenderer({ canvas: this.canvas, antialias: true, alpha: false, powerPreference: "high-performance" });
      this.renderer.outputColorSpace = THREE.SRGBColorSpace;
      this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
      this.renderer.toneMappingExposure = 1.04;
      this.renderer.setClearColor(0xc9c5bb);
      this.scene.background = new THREE.Color(0xc9c5bb);
      this.scene.fog = new THREE.Fog(0xc9c5bb, 22, 45);
      this.camera.position.copy(START_POSITION);
      this.scene.add(this.camera);
      this.buildStore();
      this.bindEvents();
      this.resize();
      this.frame = requestAnimationFrame(this.animate);
      this.onReady();
    } catch {
      this.onError();
    }
  }

  resetPosition(): void {
    this.camera.position.copy(START_POSITION);
    this.yaw = 0;
    this.pitch = -0.015;
    this.autoTarget = null;
  }

  setAgeConfirmed(confirmed: boolean): void {
    this.ageConfirmed = confirmed;
    this.agePromptRequested = false;
  }

  confirmAgeAndEnter(): void {
    this.ageConfirmed = true;
    this.agePromptRequested = false;
    const portal = this.navigationZones[0];
    if (portal) this.autoTarget = portal.destination.clone();
  }

  setMoveInput(x: number, y: number): void {
    this.moveInput.set(
      THREE.MathUtils.clamp(x, -1, 1),
      THREE.MathUtils.clamp(y, -1, 1),
    );
    if (this.moveInput.lengthSq() > 1) this.moveInput.normalize();
    if (this.moveInput.lengthSq() > 0.001) this.autoTarget = null;
  }

  setQuality(quality: Quality): void {
    this.quality = quality;
    this.applyPixelRatio();
  }

  setLanguage(language: Language): void {
    this.language = language;
    this.signs.forEach((sign) => this.updateSign(sign));
    this.norenPanels.forEach((panel) => {
      const material = panel.material as THREE.MeshStandardMaterial;
      material.map = this.createNorenTexture(panel.userData.index, language);
      material.needsUpdate = true;
    });
    if (this.hoveredZone) this.onNavigationHover(this.hoveredZone.label[language]);
  }

  setFloorGuidesHidden(hidden: boolean): void {
    this.floorGuidesHidden = hidden;
    this.navigationZones.forEach((zone) => {
      if (!zone.marker) return;
      zone.mesh.visible = !hidden;
      zone.marker.group.visible = !hidden;
    });
    if (hidden && this.hoveredZone?.marker) {
      this.hoveredZone = null;
      this.onNavigationHover(null);
    }
  }

  setSections(sections: CatalogSection[]): void {
    this.shelfImageLoadGeneration += 1;
    this.shelfImageLoadQueue.length = 0;
    this.currentSections = sections.map((section) => ({
      ...section,
      items: section.items.filter((item) => Boolean(item.images.shelf && item.images.detail)),
    }));
    this.buildStaffPicksPool();
    this.surfaceUsedIds.clear();
    this.shelfSlots.forEach((slot) => {
      delete slot.mesh.userData.assignedItemId;
      this.assignSlot(slot);
    });
  }

  // 「店長おすすめ」棚は1枚の大きな壁面なので、同じジャケット写真が
  // セクションをまたいで重複しないよう、全セクションから重複排除した
  // プールを作り、そこからシャッフルして割り当てる。
  private buildStaffPicksPool(): void {
    const seen = new Set<string>();
    const pool: CatalogItem[] = [];
    for (const section of this.currentSections) {
      for (const item of section.items) {
        if (seen.has(item.id)) continue;
        seen.add(item.id);
        pool.push(item);
      }
    }
    for (let i = pool.length - 1; i > 0; i -= 1) {
      const j = Math.floor(Math.random() * (i + 1));
      [pool[i], pool[j]] = [pool[j], pool[i]];
    }
    this.staffPicksPool = pool;
  }

  destroy(): void {
    this.destroyed = true;
    this.shelfImageLoadGeneration += 1;
    this.shelfImageLoadQueue.length = 0;
    cancelAnimationFrame(this.frame);
    this.mobileHighlightedZones.forEach((zone) => this.setZoneHighlight(zone, false));
    this.mobileHighlightedZones.clear();
    this.unbindEvents();
    const geometries = new Set<THREE.BufferGeometry>();
    const materials = new Set<THREE.Material>();
    this.scene.traverse((object) => {
      if (!(object instanceof THREE.Mesh)) return;
      geometries.add(object.geometry);
      (Array.isArray(object.material) ? object.material : [object.material]).forEach((material) => materials.add(material));
    });
    geometries.forEach((geometry) => geometry.dispose());
    materials.forEach((material) => material.dispose());
    this.textureCache.forEach((texture) => texture.dispose());
    this.renderer?.dispose();
  }

  private buildStore(): void {
    this.scene.add(new THREE.HemisphereLight(0xfaf8ef, 0x77694f, 1.88));
    const daylight = new THREE.DirectionalLight(0xfff5dc, 1.35);
    daylight.position.set(-5, 9, 7);
    this.scene.add(daylight);
    this.addFloorAndShell();
    this.addFrontGlassEntrance();
    this.addCeilingFixtures();
    this.addEntrance();
    this.addWallShelves();

    const units: Array<[number, number, number, number]> = [
      [-2.9, 5.55, 0, 1],
      [2.9, 5.55, 2, 3],
      [-2.9, -1.15, 4, 5],
      [2.9, -1.15, 6, 7],
    ];
    units.forEach(([x, z, outerSection, innerSection], index) => this.addGondolaShelf(x, z, outerSection, innerSection, index));
    this.addBackDisplay();
    this.addNavigationZones();
  }

  private addFloorAndShell(): void {
    this.floorMesh = new THREE.Mesh(
      new THREE.PlaneGeometry(16.4, 24.6),
      new THREE.MeshStandardMaterial({ color: 0xc89f58, roughness: 0.8 }),
    );
    this.floorMesh.name = "walkable-floor";
    this.floorMesh.rotation.x = -Math.PI / 2;
    this.floorMesh.position.set(0, 0, 2.9);
    this.scene.add(this.floorMesh);
    void new THREE.TextureLoader().loadAsync("/textures/maple-floor.png").then((texture) => {
      if (this.destroyed || !this.floorMesh) return texture.dispose();
      texture.colorSpace = THREE.SRGBColorSpace;
      texture.wrapS = THREE.RepeatWrapping;
      texture.wrapT = THREE.RepeatWrapping;
      texture.repeat.set(4.5, 6.5);
      texture.anisotropy = 2;
      const material = new THREE.MeshStandardMaterial({ map: texture, roughness: 0.82, metalness: 0.01, color: 0xe1c894 });
      this.textureCache.set("floor", texture);
      this.materialCache.set("floor", material);
      this.floorMesh.material = material;
    }).catch(() => undefined);

    const ceiling = new THREE.Mesh(
      new THREE.PlaneGeometry(16.4, 24.6),
      new THREE.MeshStandardMaterial({ map: this.createCeilingTexture(), color: 0xc9c6bd, roughness: 0.98, side: THREE.DoubleSide }),
    );
    ceiling.rotation.x = Math.PI / 2;
    ceiling.position.set(0, CEILING_Y, 2.9);
    this.scene.add(ceiling);
    this.addBox([0.24, CEILING_Y, 25.0], [-8.2, CEILING_Y / 2, 2.7], 0xd0ccc3, 0.94);
    this.addBox([0.24, CEILING_Y, 25.0], [8.2, CEILING_Y / 2, 2.7], 0xd0ccc3, 0.94);
    const backdrop = new THREE.Mesh(
      new THREE.PlaneGeometry(16.64, CEILING_Y),
      new THREE.MeshStandardMaterial({ color: 0xf2f0ea, roughness: 0.94, metalness: 0.02, side: THREE.DoubleSide }),
    );
    backdrop.position.set(0, CEILING_Y / 2, -9.4);
    this.scene.add(backdrop);
  }

  private createCeilingTexture(): THREE.CanvasTexture {
    const canvas = document.createElement("canvas");
    canvas.width = canvas.height = 1024;
    const context = canvas.getContext("2d");
    if (context) {
      context.fillStyle = "#c8c5bc";
      context.fillRect(0, 0, 1024, 1024);
      context.fillStyle = "rgba(70,68,63,.18)";
      for (let index = 0; index < 3200; index += 1) {
        const x = (index * 67 + index * index * 3) % 1024;
        const y = (index * 113 + index * index) % 1024;
        context.fillRect(x, y, index % 7 === 0 ? 3 : 2, 1);
      }
      context.strokeStyle = "rgba(75,72,67,.32)";
      context.lineWidth = 3;
      for (let position = 0; position <= 1024; position += 170) {
        context.beginPath(); context.moveTo(position, 0); context.lineTo(position, 1024); context.stroke();
        context.beginPath(); context.moveTo(0, position); context.lineTo(1024, position); context.stroke();
      }
    }
    const texture = new THREE.CanvasTexture(canvas);
    texture.colorSpace = THREE.SRGBColorSpace;
    texture.wrapS = texture.wrapT = THREE.RepeatWrapping;
    texture.repeat.set(3, 6);
    this.textureCache.set("ceiling", texture);
    return texture;
  }

  private addCeilingFixtures(): void {
    const lightMaterial = new THREE.MeshStandardMaterial({ color: 0xe8e5dc, emissive: 0xfff4d9, emissiveIntensity: 1.15, roughness: 0.5 });
    const lightGeometry = new THREE.BoxGeometry(1.65, 0.065, 0.68);
    for (const z of [12.3, 6.5, 0.5, -5.7]) {
      for (const x of [-3.1, 3.1]) {
        const fixture = new THREE.Mesh(lightGeometry, lightMaterial);
        fixture.position.set(x, CEILING_Y - 0.06, z);
        this.scene.add(fixture);
      }
      const point = new THREE.PointLight(0xffedcc, 4.55, 9.8, 1.85);
      point.position.set(0, CEILING_Y - 0.34, z);
      this.scene.add(point);
    }

    for (const z of [7.2, 0.2, -6.2]) {
      for (const x of [-6.65, 6.65]) {
        const fixture = new THREE.Mesh(lightGeometry, lightMaterial);
        fixture.position.set(x, CEILING_Y - 0.06, z);
        this.scene.add(fixture);
        const wallLight = new THREE.PointLight(0xffedcf, 2.68, 6.9, 1.75);
        wallLight.position.set(x, CEILING_Y - 0.42, z);
        this.scene.add(wallLight);
      }
    }

    for (const [x, z] of [[-5.7, 8.8], [4.9, 1.2], [-4.5, -6.8]] as Array<[number, number]>) {
      const speaker = new THREE.Mesh(new THREE.CylinderGeometry(0.23, 0.23, 0.045, 24), new THREE.MeshStandardMaterial({ color: 0x656560, roughness: 0.82 }));
      speaker.position.set(x, CEILING_Y - 0.045, z);
      this.scene.add(speaker);
    }

    SECURITY_CAMERAS.forEach((camera) => this.addSecurityCamera(camera.x, camera.z, camera.yaw, camera.pitch));
    HANGING_SPEAKERS.forEach((speaker) => this.addHangingSpeaker(speaker.x, speaker.z, speaker.rotationY));

    const exitGroup = new THREE.Group();
    const body = new THREE.Mesh(new THREE.BoxGeometry(1.18, 0.42, 0.12), new THREE.MeshStandardMaterial({ color: 0x168a4a, emissive: 0x0b3f24, emissiveIntensity: 0.34, roughness: 0.72 }));
    exitGroup.add(body);
    const face = new THREE.Mesh(new THREE.PlaneGeometry(1.08, 0.32), this.makeStaticTextMaterial("exit-sign", "非常口  ➜", "#168a4a", "#ffffff", 1.08 / 0.32));
    face.position.z = 0.065;
    exitGroup.add(face);
    exitGroup.position.set(-5.7, 3.48, 9.9);
    this.scene.add(exitGroup);
    this.addCylinder(0.025, 0.48, [-5.7, 3.83, 9.9], 0x5d5d58);
  }

  private addSecurityCamera(x: number, z: number, rotationY: number, pitch = 0): void {
    // mount: 天井に固定されるポール。yaw(左右)のみ。pitch は適用しないので
    // ポールは天井に対して常に垂直になる。
    const mount = new THREE.Group();
    mount.position.set(x, CEILING_Y - 0.42, z);
    mount.rotation.order = "YXZ";
    mount.rotation.y = rotationY;
    this.addCylinderTo(mount, 0.026, 0.42, [0, 0.21, 0], 0x5e5e5a);

    // head: ポール下端のボールジョイントを支点に pitch(上下)で傾く。
    const head = new THREE.Group();
    head.rotation.order = "YXZ";
    head.rotation.x = pitch;
    mount.add(head);

    const body = new THREE.Mesh(new THREE.BoxGeometry(0.43, 0.22, 0.55), new THREE.MeshStandardMaterial({ color: 0x9b9c99, roughness: 0.58, metalness: 0.18 }));
    body.position.y = -0.08;
    body.rotation.x = -0.2;
    head.add(body);
    const lens = new THREE.Mesh(new THREE.CylinderGeometry(0.07, 0.07, 0.025, 20), new THREE.MeshStandardMaterial({ color: 0x111315, roughness: 0.3 }));
    lens.rotation.x = Math.PI / 2;
    lens.position.set(0, -0.12, 0.29);
    head.add(lens);
    this.scene.add(mount);
  }

  private addHangingSpeaker(x: number, z: number, rotationY: number): void {
    const group = new THREE.Group();
    this.addCylinderTo(group, 0.024, 0.42, [0, 0.21, 0], 0x4b4b48);
    const speaker = new THREE.Mesh(new THREE.BoxGeometry(0.38, 0.42, 0.34), new THREE.MeshStandardMaterial({ color: 0x303236, roughness: 0.7 }));
    speaker.position.y = -0.18;
    group.add(speaker);
    group.position.set(x, CEILING_Y - 0.42, z);
    group.rotation.y = rotationY;
    this.scene.add(group);
  }

  private addEntrance(): void {
    const sideWidth = 6.50;
    const centerNorenWidth = 1.815;
    const sideNorenX = centerNorenWidth + 1.05;
    for (const side of [-1, 1]) {
      const centerX = side * (centerNorenWidth + sideWidth / 2);
      this.addBox([sideWidth, CEILING_Y, 0.42], [centerX, CEILING_Y / 2, NOREN_Z], 0x2d2c29, 0.88);
      this.addBox([2.05, 3.54, 0.06], [side * sideNorenX, 1.77, NOREN_Z + 0.24], 0x8d1728, 0.86);
      const sideFabric = new THREE.Mesh(
        new THREE.PlaneGeometry(1.92, 3.36),
        new THREE.MeshStandardMaterial({ map: this.createSideNorenTexture(side), roughness: 0.92, side: THREE.DoubleSide }),
      );
      sideFabric.position.set(side * sideNorenX, 1.78, NOREN_Z + 0.28);
      this.scene.add(sideFabric);
    }
    this.collisions.push({ minX: -8.2, maxX: -(centerNorenWidth - 0.04), minZ: NOREN_Z - 0.35, maxZ: NOREN_Z + 0.45 });
    this.collisions.push({ minX: centerNorenWidth - 0.04, maxX: 8.2, minZ: NOREN_Z - 0.35, maxZ: NOREN_Z + 0.45 });

    this.addBox([3.71, 0.24, 0.34], [0, 3.84, NOREN_Z], 0x1c1b1a, 0.72);
    this.addBox([3.57, 0.09, 0.1], [0, 3.54, NOREN_Z + 0.24], 0x141414, 0.5);
    this.addLanguagePlane([0, 3.84, NOREN_Z + 0.34], [3.34, 0.42], 0, "アダルトコーナー", "ADULT CORNER", "#efe9dc", "#181616", "entry");

    for (let index = 0; index < 2; index += 1) {
      const x = index === 0 ? -centerNorenWidth / 2 : centerNorenWidth / 2;
      const texture = this.createNorenTexture(index, this.language);
      const panel = new THREE.Mesh(
        new THREE.PlaneGeometry(centerNorenWidth, 3.43, 5, 14),
        new THREE.MeshStandardMaterial({ map: texture, roughness: 0.94, side: THREE.DoubleSide }),
      ) as unknown as NorenPanel;
      panel.position.set(x, 1.78, NOREN_Z + 0.27);
      panel.userData = { baseX: x, direction: index === 0 ? -1 : 1, index };
      this.norenPanels.push(panel);
      this.scene.add(panel);
    }
    this.addEntranceGeneralShelves();
  }

  private addEntranceGeneralShelves(): void {
    const spineColors = [0x365d82, 0xb64c43, 0xe0b83a, 0x5b7b58, 0xd7d2c5, 0x735b8d, 0x3d777c];
    const spineMaterials = spineColors.map((color) => new THREE.MeshStandardMaterial({ color, roughness: 0.68, metalness: 0.015 }));
    const spineGeometry = new THREE.BoxGeometry(0.11, 0.54, 0.085);
    // 入り口両サイド（side = -1: 左、1: 右）の一般棚を構築
    for (const side of [-1, 1]) {
      // 棚の横幅（x軸方向のサイズ）。この値を変えると、以下の棚本体・棚板・本のx配置がすべて連動します。
      const shelfWidth = 7.34;
      // 棚の背板・本体（幅 shelfWidth、奥行 4.64）
      this.addBox([shelfWidth, 3.35, 4.64], [side * 7.98, 1.68, 12.88], 0x292c2d, 0.74);
      // 本棚を不透過（非通過）にするため、本体の占有範囲を衝突判定 rect として登録する。
      // 幅 shelfWidth・奥行 4.64・中心 (side*7.98, 12.88) の枠内にはプレイヤーが侵入できなくなる。
      this.collisions.push({
        minX: side * 7.98 - shelfWidth / 2,
        maxX: side * 7.98 + shelfWidth / 2,
        minZ: 12.88 - 4.64 / 2,
        maxZ: 12.88 + 4.64 / 2,
      });
      // 各段の棚板（幅 shelfWidth-0.2、奥行 4.5、厚み 0.055）
      for (const y of [0.22, 0.9, 1.58, 2.26, 2.94, 3.28]) {
        this.addBox([shelfWidth - 0.2, 0.055, 4.5], [side * 7.98, y, 12.88], 0x151718, 0.5);
      }
      // row: 段（高さ方向）、column: 列（奥行 z 方向の本の配置位置）
      // ── 本のx軸位置を調整する箇所 ──
      // spineFaceX が「本（背表紙）のx座標」です。本は棚の内側面（店の中心を向く面）に沿って並ぶため、
      // 「棚中心x(7.98) − 半幅(shelfWidth/2) − 内側へのわずかな押し込み(0.09)」で内側面の手前位置を求めています。
      // ・本ごとにx軸方向へまとめて動かす → この式の末尾の 0.09（オフセット）を増減させてください。
      // ・棚の横幅ごと動かす → shelfWidth を変えれば本体・棚板・本のxが自動で連動します。
      const spineFaceX = side * (7.98 - shelfWidth / 2 - 0.09);
      const spineZMin = 12.88 - 4.64 / 2 + 0.1;
      const spineZMax = 12.88 + 4.64 / 2 - 0.1;
      const spineRows = 5;
      const spineCols = 44;
      for (let row = 0; row < spineRows; row += 1) {
        for (let column = 0; column < spineCols; column += 1) {
          // 本の背表紙（ダミー表紙）
          const spine = new THREE.Mesh(spineGeometry, spineMaterials[(row * 5 + column) % spineMaterials.length]);
          spine.position.set(spineFaceX, 0.5 + row * 0.62, spineZMin + (spineZMax - spineZMin) * (column / (spineCols - 1)));
          spine.rotation.x = ((column % 5) - 2) * 0.006;
          this.scene.add(spine);
        }
      }
    }
  }

  private createNorenTexture(index: number, language: Language): THREE.CanvasTexture {
    const key = `noren-main-${index}-${language}`;
    const cached = this.textureCache.get(key);
    if (cached) return cached as THREE.CanvasTexture;
    const artwork = document.createElement("canvas");
    artwork.width = 954;
    artwork.height = 900;
    const artworkContext = artwork.getContext("2d");
    if (artworkContext) {
      artworkContext.fillStyle = "#111315";
      artworkContext.fillRect(0, 0, artwork.width, artwork.height);
      const fabric = artworkContext.createLinearGradient(0, 0, artwork.width, 0);
      fabric.addColorStop(0, "rgba(255,255,255,.055)");
      fabric.addColorStop(0.49, "rgba(255,255,255,.008)");
      fabric.addColorStop(0.51, "rgba(0,0,0,.11)");
      fabric.addColorStop(1, "rgba(0,0,0,.22)");
      artworkContext.fillStyle = fabric;
      artworkContext.fillRect(0, 0, artwork.width, artwork.height);
      artworkContext.strokeStyle = "rgba(255,255,255,.04)";
      artworkContext.lineWidth = 2;
      for (let x = 15; x < artwork.width; x += 19) {
        artworkContext.beginPath(); artworkContext.moveTo(x, 0); artworkContext.lineTo(x, artwork.height); artworkContext.stroke();
      }

      artworkContext.save();
      artworkContext.translate(477, 338);
      artworkContext.strokeStyle = "#db001b";
      artworkContext.lineCap = "round";
      artworkContext.lineWidth = 27;
      artworkContext.beginPath(); artworkContext.arc(0, 0, 163, 0, Math.PI * 2); artworkContext.stroke();
      artworkContext.fillStyle = "#eeeae2";
      artworkContext.textAlign = "center";
      artworkContext.textBaseline = "middle";
      artworkContext.font = "900 183px 'Arial Black', sans-serif";
      artworkContext.fillText("18", 0, 10);
      artworkContext.strokeStyle = "#db001b";
      artworkContext.lineWidth = 30;
      artworkContext.beginPath(); artworkContext.moveTo(-99, -102); artworkContext.lineTo(100, 102); artworkContext.stroke();
      artworkContext.restore();

      artworkContext.fillStyle = "#eeeae2";
      artworkContext.textAlign = "center";
      artworkContext.textBaseline = "middle";
      artworkContext.font = language === "ja" ? "900 43px sans-serif" : "900 37px sans-serif";
      artworkContext.fillText(language === "ja" ? "18歳未満 立入禁止" : "NO ENTRY UNDER 18", 477, 690);
      artworkContext.fillStyle = "#b5b0a7";
      artworkContext.font = "800 21px sans-serif";
      artworkContext.fillText("QUIYA", 477, 757);
    }
    const canvas = document.createElement("canvas");
    canvas.width = 477;
    canvas.height = 900;
    const context = canvas.getContext("2d");
    context?.drawImage(artwork, index * 477, 0, 477, 900, 0, 0, 477, 900);
    const texture = new THREE.CanvasTexture(canvas);
    texture.colorSpace = THREE.SRGBColorSpace;
    this.textureCache.set(key, texture);
    return texture;
  }

  private createSideNorenTexture(side: number): THREE.CanvasTexture {
    const key = `noren-side-${side}`;
    const canvas = document.createElement("canvas");
    canvas.width = 420;
    canvas.height = 760;
    const context = canvas.getContext("2d");
    if (context) {
      context.fillStyle = "#8d1728";
      context.fillRect(0, 0, 420, 760);
      context.fillStyle = "rgba(0,0,0,.14)";
      for (let x = 0; x < 420; x += 28) context.fillRect(x, 0, 2, 760);
      context.strokeStyle = "#eee7db";
      context.lineWidth = 16;
      context.beginPath(); context.arc(210, 310, 116, 0, Math.PI * 2); context.stroke();
      context.fillStyle = "#f4eee3";
      context.textAlign = "center";
      context.font = "900 114px sans-serif";
      context.fillText("18", 210, 350);
      context.strokeStyle = "#ffffff";
      context.lineCap = "butt";
      context.lineWidth = 22;
      context.beginPath();
      context.moveTo(129, 227);
      context.lineTo(291, 393);
      context.stroke();
      context.font = "800 28px sans-serif";
      context.fillText("QUIYA", 210, 550);
    }
    const texture = new THREE.CanvasTexture(canvas);
    texture.colorSpace = THREE.SRGBColorSpace;
    this.textureCache.set(key, texture);
    return texture;
  }

  private addWallShelves(): void {
    for (const side of [-1, 1]) {
      const x = side * 7.82;
      const sectionIndex = side < 0 ? 8 : 9;
      this.addBox([0.62, 3.34, 16.3], [x, 1.68, 1.3], 0x272929, 0.74);
      const label = SECTION_LABELS[sectionIndex];
      this.addHangingSign(
        [side * 7.02, 3.42, 1.3],
        side < 0 ? Math.PI / 2 : -Math.PI / 2,
        5.4,
        0.62,
        label.ja,
        label.en,
        label.accent,
        `wall-${side}`,
      );
      for (const y of [0.23, 0.93, 1.63, 2.33, 2.93]) {
        this.addBox([0.82, 0.065, 16.4], [side * 7.51, y, 1.3], 0x131515, 0.5);
      }
      const geometry = new THREE.BoxGeometry(0.46, 0.62, 0.035);
      for (let row = 0; row < 4; row += 1) {
        for (let column = 0; column < 29; column += 1) {
          const mesh = new THREE.Mesh(geometry, this.getPlaceholderMaterial(sectionIndex, row, column));
          mesh.rotation.y = side < 0 ? Math.PI / 2 : -Math.PI / 2;
          mesh.position.set(side * 7.22, 0.58 + row * 0.7, -5.92 + column * 0.515);
          this.addProductSlot(mesh, sectionIndex, column * 4 + row, `wall-${side}`);
        }
      }
      this.collisions.push({ minX: side < 0 ? -8.2 : 7.25, maxX: side < 0 ? -7.25 : 8.2, minZ: -6.95, maxZ: 9.4 });
    }
  }

  private addGondolaShelf(x: number, z: number, outerSection: number, innerSection: number, shelfIndex: number): void {
    const bodyMaterial = new THREE.MeshStandardMaterial({ color: 0x242626, roughness: 0.72, metalness: 0.08 });
    const shape = new THREE.Shape();
    shape.moveTo(-1.22, 0); shape.lineTo(1.22, 0); shape.lineTo(0.86, 3.18); shape.lineTo(-0.86, 3.18); shape.closePath();
    const endGeometry = new THREE.ExtrudeGeometry(shape, { depth: 0.12, bevelEnabled: false });
    for (const end of [-1, 1]) {
      const panel = new THREE.Mesh(endGeometry, bodyMaterial);
      panel.position.set(x, 0, z + end * 2.58 - (end < 0 ? 0.12 : 0));
      this.scene.add(panel);
    }
    this.addBox([1.76, 3.06, 5.1], [x, 1.57, z], 0x2c2e2e, 0.75);
    for (let row = 0; row < 5; row += 1) this.addBox([2.46 - row * 0.13, 0.065, 5.28], [x, 0.23 + row * 0.7, z], 0x141616, 0.48);

    const leftSection = x < 0 ? outerSection : innerSection;
    const rightSection = x < 0 ? innerSection : outerSection;
    const surfaceKey = `gondola-${shelfIndex}`;
    this.addSectionFace(x, z, -1, leftSection, surfaceKey);
    this.addSectionFace(x, z, 1, rightSection, surfaceKey);

    if (shelfIndex < 2) this.addOuterEndcap(x, z + 2.65, rightSection, 1, surfaceKey);
    if (shelfIndex >= 2) this.addOuterEndcap(x, z - 2.65, leftSection, -1, surfaceKey);
    this.collisions.push({ minX: x - 1.38, maxX: x + 1.38, minZ: z - 2.82, maxZ: z + 2.82 });
  }

  private addSectionFace(x: number, z: number, side: -1 | 1, sectionIndex: number, surfaceKey: string): void {
    const label = SECTION_LABELS[sectionIndex];
    this.addHangingSign(
      [x + side * 1.06, 3.42, z],
      side < 0 ? -Math.PI / 2 : Math.PI / 2,
      2.6,
      0.56,
      label.ja,
      label.en,
      label.accent,
      `section-${sectionIndex}`,
    );
    const geometry = new THREE.BoxGeometry(0.46, 0.62, 0.035);
    for (let row = 0; row < 4; row += 1) {
      const faceX = x + side * (1.15 - row * 0.083);
      for (let column = 0; column < 9; column += 1) {
        const mesh = new THREE.Mesh(geometry, this.getPlaceholderMaterial(sectionIndex, row, column));
        mesh.position.set(faceX, 0.58 + row * 0.7, z - 2.06 + column * 0.515);
        mesh.rotation.y = side < 0 ? -Math.PI / 2 : Math.PI / 2;
        this.addProductSlot(mesh, sectionIndex, row * 9 + column, surfaceKey);
      }
    }
  }

  private addOuterEndcap(x: number, z: number, sectionIndex: number, direction: -1 | 1, surfaceKey: string): void {
    const geometry = new THREE.BoxGeometry(0.46, 0.62, 0.035);
    for (let column = 0; column < 3; column += 1) {
      for (let row = 0; row < 2; row += 1) {
        const mesh = new THREE.Mesh(geometry, this.getPlaceholderMaterial(sectionIndex, row, column));
        mesh.position.set(x - 0.53 + column * 0.53, 1.08 + row * 0.72, z + direction * 0.075);
        mesh.rotation.y = direction < 0 ? Math.PI : 0;
        this.addProductSlot(mesh, sectionIndex, 36 + row * 3 + column, surfaceKey);
      }
    }
  }

  private addBackDisplay(): void {
    this.addBox([14.6, 3.35, 0.66], [0, 1.68, -8.9], 0x242626, 0.72);
    for (const y of [0.22, 0.96, 1.7, 2.44, 3.18]) this.addBox([14.75, 0.065, 0.84], [0, y, -8.53], 0x141616, 0.48);
    this.addHangingSign([0, 3.42, -8.07], 0, 5.4, 0.62, "店長おすすめ!!", "STAFF PICKS!!", "#163f72", "staff-picks");
    const geometry = new THREE.BoxGeometry(0.46, 0.62, 0.035);
    for (let column = 0; column < 24; column += 1) {
      for (let row = 0; row < 3; row += 1) {
        const mesh = new THREE.Mesh(geometry, this.getPlaceholderMaterial(0, row, column));
        mesh.position.set(-6.2 + column * 0.54, 0.58 + row * 0.74, -8.43);
        this.addProductSlot(mesh, column % 4, row * 6 + Math.floor(column / 4), "back-display");
      }
    }
    this.collisions.push({ minX: -7.4, maxX: 7.4, minZ: -9.35, maxZ: -8.1 });
  }

  private addHangingSign(
    position: [number, number, number], rotationY: number, width: number, height: number,
    ja: string, en: string, accent: string, key: string,
  ): void {
    const group = new THREE.Group();
    const body = new THREE.Mesh(new THREE.BoxGeometry(width, height, 0.13), new THREE.MeshStandardMaterial({ color: new THREE.Color(accent), roughness: 0.62, metalness: 0.06 }));
    group.add(body);
    const front = new THREE.Mesh(new THREE.PlaneGeometry(width - 0.1, height - 0.1), new THREE.MeshBasicMaterial());
    front.position.z = 0.068;
    group.add(front);
    const back = new THREE.Mesh(new THREE.PlaneGeometry(width - 0.1, height - 0.1), new THREE.MeshBasicMaterial());
    back.position.z = -0.068;
    back.rotation.y = Math.PI;
    group.add(back);
    const cordLength = Math.max(0.25, CEILING_Y - (position[1] + height / 2));
    for (const x of [-width * 0.39, width * 0.39]) {
      this.addCylinderTo(group, 0.011, cordLength, [x, height / 2 + cordLength / 2, 0], 0x343331);
      const cap = new THREE.Mesh(new THREE.CylinderGeometry(0.055, 0.055, 0.025, 18), new THREE.MeshStandardMaterial({ color: 0x55534e, roughness: 0.75 }));
      cap.position.set(x, height / 2 + cordLength, 0);
      group.add(cap);
    }
    group.position.set(...position);
    group.rotation.y = rotationY;
    this.scene.add(group);
    const record = { front, back, ja, en, accent, key, aspect: (width - 0.1) / (height - 0.1) };
    this.signs.push(record);
    this.updateSign(record);
  }

  private addLanguagePlane(
    position: [number, number, number], size: [number, number], rotationY: number,
    ja: string, en: string, background: string, foreground: string, key: string,
  ): void {
    const front = new THREE.Mesh(new THREE.PlaneGeometry(...size), new THREE.MeshBasicMaterial());
    front.position.set(...position);
    front.rotation.y = rotationY;
    this.scene.add(front);
    const back = front.clone();
    back.position.z -= 0.02;
    back.rotation.y += Math.PI;
    this.scene.add(back);
    const record = { front, back, ja, en, accent: background, key: `${key}:${foreground}`, aspect: size[0] / size[1] };
    this.signs.push(record);
    this.updateSign(record, foreground);
  }

  private updateSign(sign: SignRecord, foreground = "#fffaf0"): void {
    const keyForeground = sign.key.includes(":") ? sign.key.split(":").at(-1) ?? foreground : foreground;
    const text = this.language === "ja" ? sign.ja : sign.en;
    const material = this.makeStaticTextMaterial(`sign:${sign.key}:${this.language}`, text, sign.accent, keyForeground, sign.aspect);
    sign.front.material = material;
    sign.back.material = material;
  }

  private makeStaticTextMaterial(key: string, text: string, background: string, foreground: string, aspect = 1200 / 260): THREE.MeshBasicMaterial {
    const cached = this.materialCache.get(key);
    if (cached instanceof THREE.MeshBasicMaterial) return cached;
    const canvas = document.createElement("canvas");
    canvas.height = 256;
    canvas.width = Math.max(320, Math.round(canvas.height * aspect));
    const context = canvas.getContext("2d");
    if (context) {
      context.fillStyle = background;
      context.fillRect(0, 0, canvas.width, canvas.height);
      context.fillStyle = "rgba(255,255,255,.12)";
      context.fillRect(10, 10, canvas.width - 20, 8);
      context.fillStyle = foreground;
      context.textAlign = "center";
      context.textBaseline = "middle";
      let fontSize = 138;
      do {
        context.font = `900 ${fontSize}px sans-serif`;
        if (context.measureText(text).width <= canvas.width - 68) break;
        fontSize -= 4;
      } while (fontSize > 42);
      context.fillText(text, canvas.width / 2, 126);
      context.fillStyle = "#e6c42e";
      context.fillRect(0, canvas.height - 19, canvas.width, 19);
    }
    const texture = new THREE.CanvasTexture(canvas);
    texture.colorSpace = THREE.SRGBColorSpace;
    texture.anisotropy = 2;
    const material = new THREE.MeshBasicMaterial({ map: texture, side: THREE.DoubleSide, toneMapped: false });
    this.textureCache.set(key, texture);
    this.materialCache.set(key, material);
    return material;
  }

  private addNavigationZones(): void {
    this.addPortalZone();
    const xEdges = [-7.2, -4.28, -1.52, 1.52, 4.28, 7.2];
    const zEdges = [-7.95, -4.03, 1.67, 2.73, 8.37, 9.42];
    for (let zIndex = 0; zIndex < zEdges.length - 1; zIndex += 1) {
      for (let xIndex = 0; xIndex < xEdges.length - 1; xIndex += 1) {
        const minX = xEdges[xIndex];
        const maxX = xEdges[xIndex + 1];
        const minZ = zEdges[zIndex];
        const maxZ = zEdges[zIndex + 1];
        const x = (minX + maxX) / 2;
        const z = (minZ + maxZ) / 2;
        if (!this.canMoveTo(x, z)) continue;
        const sideJa = x < -1.6 ? "左通路" : x > 1.6 ? "右通路" : "中央通路";
        const sideEn = x < -1.6 ? "LEFT AISLE" : x > 1.6 ? "RIGHT AISLE" : "CENTRAL AISLE";
        const depthJa = z < -4 ? "最奥" : z < 2 ? "中央" : "手前";
        const depthEn = z < -4 ? "BACK WALL" : z < 2 ? "MIDDLE" : "FRONT";
        this.addFloorNavigationZone(x, z, maxX - minX, maxZ - minZ, `${sideJa}・${depthJa}`, `${sideEn} · ${depthEn}`);
      }
    }
  }

  private addPortalZone(): void {
    const material = new THREE.MeshBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0.001, side: THREE.DoubleSide, depthWrite: false });
    const mesh = new THREE.Mesh(new THREE.PlaneGeometry(3.60, 3.45), material);
    mesh.position.set(0, 1.78, NOREN_Z + 0.31);
    mesh.renderOrder = 8;
    this.scene.add(mesh);
    this.navigationZones.push({ mesh, marker: null, destination: new THREE.Vector3(0, PLAYER_HEIGHT, 9.0), label: { ja: "アダルトコーナーへ入る", en: "ENTER ADULT CORNER" } });
  }

  private addFloorNavigationZone(x: number, z: number, width: number, depth: number, ja: string, en: string): void {
    // 96% on each axis is about 92% of the original zone area. The cool mint
    // tint remains legible on the maple/yellow floor without competing with it.
    const visualWidth = Math.max(0.05, width * 0.96);
    const visualDepth = Math.max(0.05, depth * 0.96);
    const radius = Math.min(0.32, visualWidth * 0.16, visualDepth * 0.16);
    const shape = this.makeRoundedRectShape(visualWidth, visualDepth, radius);
    const material = new THREE.MeshBasicMaterial({ color: 0xb8ded8, transparent: true, opacity: 0.012, side: THREE.DoubleSide, depthWrite: false });
    const mesh = new THREE.Mesh(new THREE.ShapeGeometry(shape), material);
    mesh.rotation.x = -Math.PI / 2;
    mesh.position.set(x, 0.018, z);
    mesh.renderOrder = 4;
    this.scene.add(mesh);

    const outerMaterial = new THREE.MeshBasicMaterial({ color: 0xe4f3ef, transparent: true, opacity: 0.18, side: THREE.DoubleSide, depthWrite: false });
    const innerMaterial = new THREE.MeshBasicMaterial({ color: 0x78aaa5, transparent: true, opacity: 0.12, side: THREE.DoubleSide, depthWrite: false });
    const centerMaterial = new THREE.MeshBasicMaterial({ color: 0xf3fbf8, transparent: true, opacity: 0.055, side: THREE.DoubleSide, depthWrite: false });
    const outer = new THREE.Mesh(this.makeRoundedRectRing(visualWidth, visualDepth, radius, 0.065), outerMaterial);
    const inner = new THREE.Mesh(this.makeRoundedRectRing(visualWidth - 0.17, visualDepth - 0.17, Math.max(0.04, radius - 0.085), 0.025), innerMaterial);
    const center = new THREE.Mesh(new THREE.CircleGeometry(Math.min(visualWidth, visualDepth) * 0.075, 32), centerMaterial);
    const markerGroup = new THREE.Group();
    markerGroup.add(outer, inner, center);
    markerGroup.rotation.x = -Math.PI / 2;
    markerGroup.position.set(x, 0.026, z);
    markerGroup.renderOrder = 5;
    markerGroup.visible = !this.floorGuidesHidden;
    mesh.visible = !this.floorGuidesHidden;
    this.scene.add(markerGroup);
    const marker: NavigationMarker = { group: markerGroup, outerMaterial, innerMaterial, centerMaterial };
    this.navigationZones.push({ mesh, marker, destination: new THREE.Vector3(x, PLAYER_HEIGHT, z), label: { ja, en } });
  }

  private addFrontGlassEntrance(): void {
    const facadeZ = 15.16;
    const openingWidth = 7.5;
    const viewWidth = 7.25;
    const viewHeight = viewWidth * 9 / 16;

    // The generated photograph is the exterior beyond the glazing. Keeping it
    // on a separate unlit plane preserves the natural dusk exposure while the
    // physical frame and glass still react to the store's 3D lighting.
    const viewMaterial = new THREE.MeshBasicMaterial({ color: 0x485865, side: THREE.DoubleSide, toneMapped: false });
    const exterior = new THREE.Mesh(new THREE.PlaneGeometry(viewWidth, viewHeight), viewMaterial);
    exterior.name = "entrance-exterior-view";
    exterior.position.set(0, CEILING_Y / 2, facadeZ + 0.015);
    exterior.rotation.y = Math.PI;
    this.scene.add(exterior);
    void new THREE.TextureLoader().loadAsync("/textures/rental-store-parking-dusk.webp").then((texture) => {
      if (this.destroyed) return texture.dispose();
      texture.colorSpace = THREE.SRGBColorSpace;
      texture.anisotropy = 4;
      this.textureCache.set("entrance-exterior-view", texture);
      viewMaterial.map = texture;
      viewMaterial.color.set(0xffffff);
      viewMaterial.needsUpdate = true;
    }).catch(() => undefined);

    // White wall remaining around the central glass opening.
    const sideWallWidth = (16.4 - openingWidth) / 2;
    const wallMaterial = new THREE.MeshStandardMaterial({ color: 0xe7e4dc, roughness: 0.92, metalness: 0.01 });
    for (const side of [-1, 1]) {
      const wall = new THREE.Mesh(new THREE.BoxGeometry(sideWallWidth, CEILING_Y, 0.28), wallMaterial);
      wall.position.set(side * (openingWidth / 2 + sideWallWidth / 2), CEILING_Y / 2, facadeZ + 0.14);
      this.scene.add(wall);
    }

    const frameMaterial = new THREE.MeshStandardMaterial({ color: 0x33444e, roughness: 0.38, metalness: 0.28 });
    const addFrame = (width: number, height: number, x: number, y: number): void => {
      const frame = new THREE.Mesh(new THREE.BoxGeometry(width, height, 0.105), frameMaterial);
      frame.position.set(x, y, facadeZ - 0.06);
      this.scene.add(frame);
    };
    addFrame(openingWidth + 0.16, 0.15, 0, 0.075);
    addFrame(openingWidth + 0.16, 0.15, 0, CEILING_Y - 0.075);
    for (const x of [-openingWidth / 2, -1.5, 0, 1.5, openingWidth / 2]) addFrame(0.105, CEILING_Y, x, CEILING_Y / 2);
    addFrame(openingWidth, 0.085, 0, 3.32);

    const glass = new THREE.Mesh(
      new THREE.PlaneGeometry(openingWidth - 0.12, CEILING_Y - 0.18),
      new THREE.MeshPhysicalMaterial({
        color: 0xc9e0e6,
        transparent: true,
        opacity: 0.075,
        roughness: 0.12,
        metalness: 0.04,
        side: THREE.DoubleSide,
        depthWrite: false,
      }),
    );
    glass.name = "entrance-glass";
    glass.position.set(0, CEILING_Y / 2, facadeZ - 0.12);
    glass.renderOrder = 2;
    this.scene.add(glass);

    // Small paired safety decals make the center panes read as automatic
    // sliding doors without baking text or branding into the exterior photo.
    const decalMaterial = new THREE.MeshBasicMaterial({ color: 0xe7edf0, transparent: true, opacity: 0.58, depthWrite: false });
    for (const x of [-0.23, 0.23]) {
      const decal = new THREE.Mesh(new THREE.CircleGeometry(0.045, 20), decalMaterial);
      decal.position.set(x, 1.26, facadeZ - 0.185);
      decal.rotation.y = Math.PI;
      decal.renderOrder = 3;
      this.scene.add(decal);
    }
  }

  private makeRoundedRectShape(width: number, height: number, radius: number): THREE.Shape {
    const shape = new THREE.Shape();
    const left = -width / 2;
    const right = width / 2;
    const bottom = -height / 2;
    const top = height / 2;
    shape.moveTo(left + radius, bottom);
    shape.lineTo(right - radius, bottom);
    shape.quadraticCurveTo(right, bottom, right, bottom + radius);
    shape.lineTo(right, top - radius);
    shape.quadraticCurveTo(right, top, right - radius, top);
    shape.lineTo(left + radius, top);
    shape.quadraticCurveTo(left, top, left, top - radius);
    shape.lineTo(left, bottom + radius);
    shape.quadraticCurveTo(left, bottom, left + radius, bottom);
    return shape;
  }

  private makeRoundedRectRing(width: number, height: number, radius: number, thickness: number): THREE.ShapeGeometry {
    const outer = this.makeRoundedRectShape(width, height, radius);
    const innerWidth = Math.max(0.02, width - thickness * 2);
    const innerHeight = Math.max(0.02, height - thickness * 2);
    const innerRadius = Math.max(0.01, radius - thickness);
    const innerShape = this.makeRoundedRectShape(innerWidth, innerHeight, innerRadius);
    const hole = new THREE.Path(innerShape.getPoints(24).reverse());
    outer.holes.push(hole);
    return new THREE.ShapeGeometry(outer, 24);
  }

  private addProductSlot(mesh: THREE.Mesh, sectionIndex: number, itemIndex: number, surfaceKey: string): void {
    mesh.userData.basePosition = mesh.position.clone();
    mesh.userData.baseScale = mesh.scale.clone();
    this.productMeshes.push(mesh);
    this.shelfSlots.push({ mesh, sectionIndex, itemIndex, surfaceKey });
    this.scene.add(mesh);
  }

  private assignSlot(slot: ShelfSlot, shift = 0): void {
    if (slot.surfaceKey === "back-display") {
      this.assignStaffPicksSlot(slot, shift);
      return;
    }
    const section = this.currentSections[slot.sectionIndex % Math.max(this.currentSections.length, 1)];
    // Dedup per face/section, not per whole gondola: both faces of a gondola
    // share one surfaceKey but show different sections, so keying only by
    // surfaceKey would block a product that legitimately appears in both.
    const usedIds = this.getOrCreateUsedIds(`${slot.surfaceKey}:${slot.sectionIndex}`);
    const previousId = slot.mesh.userData.assignedItemId as string | undefined;
    if (previousId) usedIds.delete(previousId);
    if (!section || section.items.length === 0) {
      slot.mesh.visible = false;
      return;
    }
    for (let attempt = 0; attempt < section.items.length; attempt += 1) {
      const itemIndex = (slot.itemIndex + shift + attempt) % section.items.length;
      const item = section.items[itemIndex];
      if (!item.images.shelf || this.failedItemIds.has(item.id) || usedIds.has(item.id)) continue;
      usedIds.add(item.id);
      this.commitSlotAssignment(slot, item, itemIndex, usedIds, shift + attempt + 1);
      return;
    }
    slot.mesh.visible = false;
    delete slot.mesh.userData.product;
    delete slot.mesh.userData.assignedItemId;
  }

  // 「店長おすすめ」棚はセクションをまたがない1面の壁なので、重複排除済みの
  // プールから順に割り当て、同じジャケットが2度出ないようにする。プールが
  // 枯渇したらその枠は空（非表示）にする。
  private assignStaffPicksSlot(slot: ShelfSlot, shift: number): void {
    const pool = this.staffPicksPool;
    const usedIds = this.getOrCreateUsedIds("back-display");
    const previousId = slot.mesh.userData.assignedItemId as string | undefined;
    if (previousId) usedIds.delete(previousId);
    if (pool.length === 0) {
      slot.mesh.visible = false;
      return;
    }
    for (let attempt = 0; attempt < pool.length; attempt += 1) {
      const item = pool[(slot.itemIndex + shift + attempt) % pool.length];
      if (this.failedItemIds.has(item.id) || usedIds.has(item.id)) continue;
      usedIds.add(item.id);
      this.commitSlotAssignment(slot, item, slot.itemIndex, usedIds, shift + attempt + 1);
      return;
    }
    slot.mesh.visible = false;
    delete slot.mesh.userData.product;
    delete slot.mesh.userData.assignedItemId;
  }

  private getOrCreateUsedIds(key: string): Set<string> {
    let usedIds = this.surfaceUsedIds.get(key);
    if (!usedIds) {
      usedIds = new Set<string>();
      this.surfaceUsedIds.set(key, usedIds);
    }
    return usedIds;
  }

  private commitSlotAssignment(
    slot: ShelfSlot, item: CatalogItem, itemIndex: number, usedIds: Set<string>, retryShift: number,
  ): void {
    slot.mesh.visible = true;
    slot.mesh.userData.product = item;
    slot.mesh.userData.assignedItemId = item.id;
    slot.mesh.userData.assignment = `${item.id}:${itemIndex}:${++this.assignmentSerial}`;
    delete slot.mesh.userData.imageUrl;
    slot.mesh.material = this.getFallbackMaterial(item);
    const assignment = slot.mesh.userData.assignment;
    const generation = this.shelfImageLoadGeneration;
    this.shelfImageLoadQueue.push({
      priority: shelfFaceLoadPriority(slot.sectionIndex, slot.surfaceKey),
      sequence: this.shelfImageLoadSequence++,
      generation,
      run: async () => {
        if (this.destroyed || generation !== this.shelfImageLoadGeneration || slot.mesh.userData.assignment !== assignment) return;
        const loaded = await this.applyProductImage(item, slot.mesh, assignment);
        if (loaded || this.destroyed || generation !== this.shelfImageLoadGeneration || slot.mesh.userData.assignment !== assignment) return;
        usedIds.delete(item.id);
        if (!this.failedItemIds.has(item.id)) {
          this.failedItemIds.add(item.id);
          this.onProductUnavailable(item.id);
        }
        this.assignSlot(slot, retryShift);
      },
    });
    this.scheduleShelfImagePump();
  }

  private scheduleShelfImagePump(): void {
    if (this.shelfImagePumpScheduled || this.destroyed) return;
    this.shelfImagePumpScheduled = true;
    queueMicrotask(() => {
      this.shelfImagePumpScheduled = false;
      if (this.destroyed) return;
      this.shelfImageLoadQueue.sort((a, b) => a.priority - b.priority || a.sequence - b.sequence);
      this.pumpShelfImageLoads();
    });
  }

  private pumpShelfImageLoads(): void {
    while (this.activeShelfImageLoads < SHELF_IMAGE_LOAD_CONCURRENCY && this.shelfImageLoadQueue.length > 0) {
      const task = this.shelfImageLoadQueue.shift()!;
      if (task.generation !== this.shelfImageLoadGeneration) continue;
      this.activeShelfImageLoads += 1;
      void task.run().finally(() => {
        this.activeShelfImageLoads -= 1;
        this.scheduleShelfImagePump();
      });
    }
  }

  private async applyProductImage(item: CatalogItem, mesh: THREE.Mesh, assignment: string): Promise<boolean> {
    const detail = item.images.detail;
    const portrait = item.images.shelfHigh ?? item.images.shelf;
    const hasSpread = detail && portrait
      ? await detectCompositeCover(this.textureUrl(detail), this.textureUrl(portrait))
      : false;
    if (mesh.userData.assignment !== assignment) return false;
    if (detail && hasSpread) {
      mesh.userData.imageUrl = detail;
      if (await this.upgradeMaterial(detail, mesh, "front", assignment)) return true;
      if (mesh.userData.assignment !== assignment) return false;
      mesh.userData.imageUrl = portrait;
    } else {
      // Some makers publish a single landscape design instead of a
      // back/spine/front spread. Cropping its right side looks broken, so use
      // the first large sample (the second image on the FANZA product page)
      // when it is a portrait.
      const alternate = item.images.samples[0];
      if (alternate) {
        mesh.userData.imageUrl = alternate;
        if (await this.upgradeMaterial(alternate, mesh, "sample", assignment)) return true;
        if (mesh.userData.assignment !== assignment) return false;
      }
    }
    const shelfHigh = item.images.shelfHigh;
    if (shelfHigh) {
      mesh.userData.imageUrl = shelfHigh;
      if (await this.upgradeMaterial(shelfHigh, mesh, "small", assignment)) return true;
      if (mesh.userData.assignment !== assignment) return false;
    }
    const shelf = item.images.shelf;
    if (!shelf) return false;
    mesh.userData.imageUrl = shelf;
    return this.upgradeMaterial(shelf, mesh, "list", assignment);
  }

  private getPlaceholderMaterial(sectionIndex: number, row: number, column: number): THREE.MeshStandardMaterial {
    const key = `placeholder:${sectionIndex}:${row}:${column % 5}`;
    const cached = this.materialCache.get(key);
    if (cached instanceof THREE.MeshStandardMaterial) return cached;
    const canvas = document.createElement("canvas");
    canvas.width = 184;
    canvas.height = 256;
    const context = canvas.getContext("2d");
    if (context) {
      const palettes = [["#e8e4d8", "#9b1727"], ["#d8e5ec", "#174d82"], ["#f1d76f", "#352b1f"], ["#e3b5b9", "#771a28"]];
      const [base, ink] = palettes[(sectionIndex + row + column) % palettes.length];
      context.fillStyle = base; context.fillRect(0, 0, 184, 256);
      context.fillStyle = ink; context.fillRect(0, 0, 184, 38); context.fillRect(0, 210, 184, 46);
      context.fillStyle = "rgba(255,255,255,.82)"; context.fillRect(15, 56, 154, 92);
      context.fillStyle = ink; context.textAlign = "center"; context.font = "900 25px sans-serif"; context.fillText("QUIYA", 92, 92);
    }
    const texture = new THREE.CanvasTexture(canvas);
    texture.colorSpace = THREE.SRGBColorSpace;
    const material = new THREE.MeshStandardMaterial({ map: texture, roughness: 0.75 });
    this.textureCache.set(key, texture);
    this.materialCache.set(key, material);
    return material;
  }

  private getFallbackMaterial(item: CatalogItem): THREE.MeshStandardMaterial {
    const key = `fallback:${item.id}`;
    const cached = this.materialCache.get(key);
    if (cached instanceof THREE.MeshStandardMaterial) return cached;
    const canvas = document.createElement("canvas");
    canvas.width = 184;
    canvas.height = 256;
    const context = canvas.getContext("2d");
    if (context) {
      const hue = [...item.id].reduce((sum, character) => sum + character.charCodeAt(0), 0) % 360;
      const gradient = context.createLinearGradient(0, 0, 184, 256);
      gradient.addColorStop(0, `hsl(${hue} 48% 52%)`);
      gradient.addColorStop(1, `hsl(${(hue + 28) % 360} 58% 20%)`);
      context.fillStyle = gradient; context.fillRect(0, 0, 184, 256);
      context.fillStyle = "rgba(0,0,0,.55)"; context.fillRect(0, 0, 184, 41);
      context.fillStyle = "#fff"; context.textAlign = "center"; context.font = "900 25px sans-serif"; context.fillText("QUIYA", 92, 29);
      context.font = "700 13px sans-serif"; context.fillText(item.id.toUpperCase().slice(0, 18), 92, 230);
    }
    const texture = new THREE.CanvasTexture(canvas);
    texture.colorSpace = THREE.SRGBColorSpace;
    const material = new THREE.MeshStandardMaterial({ map: texture, roughness: 0.72 });
    this.textureCache.set(key, texture);
    this.materialCache.set(key, material);
    return material;
  }

  private async upgradeMaterial(
    url: string,
    mesh: THREE.Mesh,
    source: "list" | "small" | "front" | "sample",
    assignment: string,
  ): Promise<boolean> {
    const textureUrl = this.textureUrl(url);
    const materialKey = `cover:${source}:${textureUrl}`;
    let material = this.materialCache.get(materialKey);
    if (!material) {
      let pending = this.materialLoads.get(materialKey);
      if (!pending) {
        pending = this.loadProductMaterial(textureUrl, materialKey, source === "front").finally(() => this.materialLoads.delete(materialKey));
        this.materialLoads.set(materialKey, pending);
      }
      material = await pending ?? undefined;
    }
    if (!material) return false;
    if (!this.destroyed && mesh.userData.assignment === assignment && mesh.userData.imageUrl === url) mesh.material = material;
    return true;
  }

  private async loadProductMaterial(
    textureUrl: string,
    materialKey: string,
    cropFront: boolean,
  ): Promise<THREE.Material | null> {
    try {
      const texture = await new THREE.TextureLoader().loadAsync(textureUrl);
      const image = texture.image as { naturalWidth?: number; naturalHeight?: number; width?: number; height?: number };
      const width = image.naturalWidth ?? image.width ?? 0;
      const height = image.naturalHeight ?? image.height ?? 0;
      if (width <= 0 || height <= 0) {
        texture.dispose();
        return null;
      }
      // Portrait-only sources (`list`/`small`) are rejected when they arrive as
      // a landscape image. The front-cover crop pulls a portrait region out of
      // the landscape `detail` spread, so it must bypass that check.
      if (!cropFront && height <= width * 1.08) {
        texture.dispose();
        return null;
      }
      texture.colorSpace = THREE.SRGBColorSpace;
      texture.anisotropy = 8;
      if (cropFront && width > height * 1.08) {
        // The large image is a back/spine/front spread; keep the right-side
        // front cover (mirrors the left-side back crop used by the case viewer).
        texture.wrapS = THREE.ClampToEdgeWrapping;
        texture.repeat.set(0.47, 1);
        texture.offset.set(0.53, 0);
        texture.needsUpdate = true;
      }
      const material = new THREE.MeshStandardMaterial({
        map: texture,
        emissive: 0xffffff,
        emissiveMap: texture,
        emissiveIntensity: 0.065,
        roughness: 0.68,
      });
      this.textureCache.set(materialKey, texture);
      this.materialCache.set(materialKey, material);
      return material;
    } catch {
      return null;
    }
  }

  private textureUrl(url: string): string {
    try {
      const parsed = new URL(url, window.location.href);
      if (parsed.hostname === "awsimgsrc.dmm.co.jp" || parsed.hostname === "pics.dmm.co.jp") return `/api/image?v=5&src=${encodeURIComponent(parsed.toString())}`;
    } catch {
      return url;
    }
    return url;
  }

  private addBox(size: [number, number, number], position: [number, number, number], color: number, roughness: number): THREE.Mesh {
    const mesh = new THREE.Mesh(new THREE.BoxGeometry(...size), new THREE.MeshStandardMaterial({ color, roughness, metalness: 0.025 }));
    mesh.position.set(...position);
    this.scene.add(mesh);
    return mesh;
  }

  private addCylinder(radius: number, length: number, position: [number, number, number], color: number): THREE.Mesh {
    const mesh = new THREE.Mesh(new THREE.CylinderGeometry(radius, radius, length, 12), new THREE.MeshStandardMaterial({ color, roughness: 0.72 }));
    mesh.position.set(...position);
    this.scene.add(mesh);
    return mesh;
  }

  private addCylinderTo(group: THREE.Group, radius: number, length: number, position: [number, number, number], color: number): void {
    const mesh = new THREE.Mesh(new THREE.CylinderGeometry(radius, radius, length, 12), new THREE.MeshStandardMaterial({ color, roughness: 0.72 }));
    mesh.position.set(...position);
    group.add(mesh);
  }

  private bindEvents(): void {
    window.addEventListener("resize", this.resize);
    window.addEventListener("keydown", this.onKeyDown);
    window.addEventListener("keyup", this.onKeyUp);
    this.canvas.addEventListener("pointerdown", this.onPointerDown);
    this.canvas.addEventListener("pointermove", this.onPointerMove);
    this.canvas.addEventListener("pointerup", this.onPointerUp);
    this.canvas.addEventListener("pointercancel", this.onPointerCancel);
    this.canvas.addEventListener("pointerleave", this.onPointerLeave);
    this.canvas.addEventListener("wheel", this.onWheel, { passive: false });
    this.canvas.addEventListener("contextmenu", this.preventContextMenu);
  }

  private unbindEvents(): void {
    window.removeEventListener("resize", this.resize);
    window.removeEventListener("keydown", this.onKeyDown);
    window.removeEventListener("keyup", this.onKeyUp);
    this.canvas.removeEventListener("pointerdown", this.onPointerDown);
    this.canvas.removeEventListener("pointermove", this.onPointerMove);
    this.canvas.removeEventListener("pointerup", this.onPointerUp);
    this.canvas.removeEventListener("pointercancel", this.onPointerCancel);
    this.canvas.removeEventListener("pointerleave", this.onPointerLeave);
    this.canvas.removeEventListener("wheel", this.onWheel);
    this.canvas.removeEventListener("contextmenu", this.preventContextMenu);
  }

  private readonly preventContextMenu = (event: Event): void => event.preventDefault();
  private readonly onKeyDown = (event: KeyboardEvent): void => {
    const code = event.code || `Key${event.key.toUpperCase()}`;
    if (["KeyW", "KeyA", "KeyS", "KeyD", "ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"].includes(code)) {
      this.keys.add(code);
      this.autoTarget = null;
      event.preventDefault();
    }
  };
  private readonly onKeyUp = (event: KeyboardEvent): void => { this.keys.delete(event.code); };
  private readonly onPointerDown = (event: PointerEvent): void => {
    this.canvas.setPointerCapture(event.pointerId);
    this.canvas.classList.add("is-dragging");
    this.clearHoveredZone();
    this.setHoveredProduct(null);
    this.autoTarget = null;
    this.currentPointer = { id: event.pointerId, x: event.clientX, y: event.clientY, moved: 0 };
  };
  private readonly onPointerMove = (event: PointerEvent): void => {
    const pointer = this.currentPointer;
    if (!pointer || pointer.id !== event.pointerId) {
      this.updateNavigationHover(event.clientX, event.clientY);
      return;
    }
    const dx = event.clientX - pointer.x;
    const dy = event.clientY - pointer.y;
    pointer.moved += Math.hypot(dx, dy);
    pointer.x = event.clientX;
    pointer.y = event.clientY;
    this.yaw += dx * 0.0041;
    this.pitch = THREE.MathUtils.clamp(this.pitch + dy * 0.0036, -0.72, 0.6);
  };
  private readonly onPointerUp = (event: PointerEvent): void => {
    const pointer = this.currentPointer;
    if (!pointer || pointer.id !== event.pointerId) return;
    this.canvas.classList.remove("is-dragging");
    if (pointer.moved < 11) this.selectOrWalk(event.clientX, event.clientY);
    this.currentPointer = null;
    this.updateNavigationHover(event.clientX, event.clientY);
  };
  private readonly onPointerCancel = (): void => {
    this.canvas.classList.remove("is-dragging");
    this.currentPointer = null;
    this.clearHoveredZone();
    this.setHoveredProduct(null);
  };
  private readonly onPointerLeave = (): void => {
    if (this.currentPointer) return;
    this.clearHoveredZone();
    this.setHoveredProduct(null);
  };
  private readonly onWheel = (event: WheelEvent): void => {
    event.preventDefault();
    this.autoTarget = null;
    const direction = event.deltaY < 0 ? 1 : -1;
    const forward = new THREE.Vector3(-Math.sin(this.yaw), 0, -Math.cos(this.yaw));
    this.tryMove(forward.multiplyScalar(Math.min(Math.abs(event.deltaY) / 80, 1.15) * direction));
  };

  private updateNavigationHover(clientX: number, clientY: number): void {
    // スマホ版では床のハイライトをプレイヤーの位置・向きで駆動するため、
    // ポインター押下中のホバー強調は行わない（updateProximityZoneHighlight が代わりに担う）。
    if (this.isTouch) return;
    this.setRayFromPointer(clientX, clientY);
    if (this.camera.position.z > AGE_THRESHOLD_Z) {
      this.setHoveredProduct(null);
      const portal = this.navigationZones[0];
      const portalHit = portal ? this.raycaster.intersectObject(portal.mesh, false)[0] : null;
      this.setHoveredNavigationZone(portalHit ? portal : null, clientX, clientY);
      return;
    }
    const hoveredProduct = this.productAtRay();
    if (hoveredProduct) {
      this.clearHoveredZone();
      this.setHoveredProduct(hoveredProduct.mesh);
      this.canvas.style.cursor = "pointer";
      return;
    }
    this.setHoveredProduct(null);
    const hit = this.raycaster.intersectObjects(this.navigationZones.map((zone) => zone.mesh), false)[0];
    const zone = hit ? this.navigationZones.find((entry) => entry.mesh === hit.object) ?? null : null;
    this.setHoveredNavigationZone(zone, clientX, clientY);
  }

  private setHoveredNavigationZone(zone: NavigationZone | null, clientX?: number, clientY?: number): void {
    if (zone !== this.hoveredZone) {
      this.clearHoveredZone();
      if (zone) {
        zone.mesh.material.opacity = zone.marker ? 0.24 : 0.09;
        if (zone.marker) this.setMarkerOpacity(zone.marker, 0.82, 0.56, 0.22);
        this.hoveredZone = zone;
      }
    }
    if (zone && clientX !== undefined && clientY !== undefined) {
      this.canvas.style.cursor = "pointer";
      this.onNavigationHover(zone.label[this.language], clientX, clientY);
    } else {
      this.canvas.style.cursor = "grab";
      this.onNavigationHover(null);
    }
  }

  private clearHoveredZone(): void {
    if (this.hoveredZone) {
      this.hoveredZone.mesh.material.opacity = this.hoveredZone.marker ? 0.012 : 0.001;
      if (this.hoveredZone.marker) this.setMarkerOpacity(this.hoveredZone.marker, 0.18, 0.12, 0.055);
    }
    this.hoveredZone = null;
    this.onNavigationHover(null);
  }

  private setZoneHighlight(zone: NavigationZone, highlighted: boolean): void {
    zone.mesh.material.opacity = highlighted ? (zone.marker ? 0.15 : 0.09) : (zone.marker ? 0.012 : 0.001);
    if (zone.marker) {
      this.setMarkerOpacity(zone.marker, highlighted ? 0.58 : 0.18, highlighted ? 0.38 : 0.12, highlighted ? 0.15 : 0.055);
    }
  }

  private setMarkerOpacity(marker: NavigationMarker, outer: number, inner: number, center: number): void {
    marker.outerMaterial.opacity = outer;
    marker.innerMaterial.opacity = inner;
    marker.centerMaterial.opacity = center;
  }

  // スマホ版：プレイヤーが立っている位置の前後左右にある区画をハイライトする。
  // マウスによるホバーが存在しないため、位置と向き（yaw）から各方向の最寄り区画を探す。
  private updateProximityZoneHighlight(): void {
    if (!this.isTouch) return;
    const player = this.camera.position;
    const fx = -Math.sin(this.yaw);
    const fz = -Math.cos(this.yaw);
    const rx = Math.cos(this.yaw);
    const rz = -Math.sin(this.yaw);
    const directions = [
      { x: fx, z: fz }, // 前
      { x: -fx, z: -fz }, // 後
      { x: -rx, z: -rz }, // 左
      { x: rx, z: rz }, // 右
    ];
    // プレイヤーが立っている区画（最寄りのゾーン）はハイライトしない。
    // のれんをくぐった直後など、プレイヤーが中央通路の手前ゾーンにぴったり
    // 乗っていると、前方向のコーンがその「立ち位置」を最寄りとして選びがちで、
    // 結果として手前（立ち位置）だけが光り、前方の区画が光らなくなる。
    // そのため、実際に足下にある（最寄りかつ近接する）ゾーンを各方向の選出から除外し、
    // 前方の次の区画を正しく選ばせる。
    let standingZone: NavigationZone | null = null;
    let standingDist = Infinity;
    for (const zone of this.navigationZones) {
      if (!zone.marker) continue;
      const dist = Math.hypot(zone.destination.x - player.x, zone.destination.z - player.z);
      if (dist < standingDist) {
        standingDist = dist;
        standingZone = zone;
      }
    }
    if (standingDist > 1.5) standingZone = null;
    const wanted = new Set<NavigationZone>();
    for (const dir of directions) {
      let best: NavigationZone | null = null;
      let bestDist = Infinity;
      for (const zone of this.navigationZones) {
        if (!zone.marker) continue;
        if (zone === standingZone) continue;
        const dx = zone.destination.x - player.x;
        const dz = zone.destination.z - player.z;
        const proj = dx * dir.x + dz * dir.z;
        if (proj <= -0.05) continue;
        const len = Math.hypot(dx, dz);
        const aligned = proj / len >= 0.5;
        if (!aligned) continue;
        if (len < bestDist) {
          bestDist = len;
          best = zone;
        }
      }
      if (best) wanted.add(best);
    }
    for (const zone of this.mobileHighlightedZones) {
      if (!wanted.has(zone)) this.setZoneHighlight(zone, false);
    }
    for (const zone of wanted) {
      if (!this.mobileHighlightedZones.has(zone)) this.setZoneHighlight(zone, true);
    }
    this.mobileHighlightedZones.clear();
    wanted.forEach((zone) => this.mobileHighlightedZones.add(zone));
  }

  private selectOrWalk(clientX: number, clientY: number): void {
    this.setRayFromPointer(clientX, clientY);
    if (this.camera.position.z > AGE_THRESHOLD_Z) {
      const portal = this.navigationZones[0];
      if (portal && this.raycaster.intersectObject(portal.mesh, false)[0]) {
        // Both verified and first-time visitors walk toward the curtain. For a
        // first-time visitor, tryMove pauses at AGE_THRESHOLD_Z and opens the
        // confirmation dialog there rather than on the initial click.
        this.autoTarget = portal.destination.clone();
      }
      return;
    }
    const selectedProduct = this.productAtRay();
    if (selectedProduct) {
      this.setHoveredProduct(null);
      this.onProductSelect(selectedProduct.item);
      return;
    }
    const zoneHit = this.raycaster.intersectObjects(this.navigationZones.map((zone) => zone.mesh), false)[0];
    if (zoneHit) {
      const zone = this.navigationZones.find((entry) => entry.mesh === zoneHit.object);
      if (zone) {
        this.autoTarget = zone.destination.clone();
        return;
      }
    }
    const floorHit = this.floorMesh ? this.raycaster.intersectObject(this.floorMesh, false)[0] : null;
    if (floorHit) {
      const destination = floorHit.point.clone();
      destination.y = PLAYER_HEIGHT;
      const offset = destination.clone().sub(this.camera.position);
      offset.y = 0;
      if (offset.length() > 7) offset.setLength(7);
      this.autoTarget = this.camera.position.clone().add(offset);
      return;
    }
    const nearbyProduct = this.productNearRay();
    if (nearbyProduct) this.onProductSelect(nearbyProduct.item);
  }

  private setRayFromPointer(clientX: number, clientY: number): void {
    const rect = this.canvas.getBoundingClientRect();
    this.pointer.set(((clientX - rect.left) / rect.width) * 2 - 1, -((clientY - rect.top) / rect.height) * 2 + 1);
    this.raycaster.setFromCamera(this.pointer, this.camera);
    this.raycaster.far = 28;
  }

  private productAtRay(): ProductHit | null {
    const visibleProducts = this.productMeshes.filter((mesh) => mesh.visible && mesh.userData.product);
    const exact = this.raycaster.intersectObjects(visibleProducts, false)[0];
    if (exact?.object.userData.product) return { item: exact.object.userData.product as CatalogItem, mesh: exact.object as THREE.Mesh };
    return null;
  }

  private productNearRay(): ProductHit | null {
    let nearest: { item: CatalogItem; mesh: THREE.Mesh; distance: number } | null = null;
    const closestPoint = new THREE.Vector3();
    for (const mesh of this.productMeshes) {
      if (!mesh.visible || !mesh.userData.product || this.camera.position.distanceTo(mesh.position) > 24) continue;
      this.raycaster.ray.closestPointToPoint(mesh.position, closestPoint);
      const alongRay = closestPoint.clone().sub(this.raycaster.ray.origin).dot(this.raycaster.ray.direction);
      if (alongRay < 0 || alongRay > this.raycaster.far) continue;
      const distance = closestPoint.distanceToSquared(mesh.position);
      if (distance > 0.11 || (nearest && distance >= nearest.distance)) continue;
      nearest = { item: mesh.userData.product as CatalogItem, mesh, distance };
    }
    return nearest ? { item: nearest.item, mesh: nearest.mesh } : null;
  }

  private setHoveredProduct(mesh: THREE.Mesh | null): void {
    if (mesh === this.hoveredProductMesh) return;
    if (this.hoveredProductMesh) this.animatedProductMeshes.add(this.hoveredProductMesh);
    this.hoveredProductMesh = mesh;
    if (mesh) {
      mesh.renderOrder = 12;
      this.animatedProductMeshes.add(mesh);
    }
  }

  private updateProductHover(delta: number): void {
    const positionBlend = 1 - Math.exp(-delta * 17);
    const scaleBlend = 1 - Math.exp(-delta * 19);
    for (const mesh of this.animatedProductMeshes) {
      const basePosition = mesh.userData.basePosition as THREE.Vector3 | undefined;
      const baseScale = mesh.userData.baseScale as THREE.Vector3 | undefined;
      if (!basePosition || !baseScale) continue;
      const isHovered = mesh === this.hoveredProductMesh;
      const normal = new THREE.Vector3(0, 0, 1).applyQuaternion(mesh.quaternion);
      const targetPosition = isHovered ? basePosition.clone().addScaledVector(normal, 0.16) : basePosition;
      const targetScale = isHovered ? baseScale.clone().multiplyScalar(1.075) : baseScale;
      mesh.position.lerp(targetPosition, positionBlend);
      mesh.scale.lerp(targetScale, scaleBlend);
      if (!isHovered && mesh.position.distanceToSquared(basePosition) < 0.00001 && mesh.scale.distanceToSquared(baseScale) < 0.00001) {
        mesh.position.copy(basePosition);
        mesh.scale.copy(baseScale);
        mesh.renderOrder = 0;
        this.animatedProductMeshes.delete(mesh);
      }
    }
  }

  private readonly resize = (): void => {
    if (!this.renderer) return;
    const width = this.canvas.clientWidth || window.innerWidth;
    const height = this.canvas.clientHeight || window.innerHeight;
    this.camera.aspect = width / Math.max(height, 1);
    // A modestly wider portrait FOV reveals more of nearby shelves without the
    // heavy fisheye distortion caused by trying to match desktop horizontal FOV.
    this.camera.fov = width < 700 && height > width ? 78 : 64;
    this.camera.updateProjectionMatrix();
    this.applyPixelRatio();
    this.renderer.setSize(width, height, false);
  };

  private applyPixelRatio(): void {
    if (!this.renderer) return;
    // The cap is relative to the device's native `devicePixelRatio` rather than
    // an absolute value. Absolute caps (the old 0.72 / 1.08 / 1.7) punished
    // high-DPR phones hardest: an iPhone 15 Pro reports DPR 3, so a 1.08 cap
    // rendered the framebuffer at ~1/3 native and the browser upscaled it,
    // blurring the jacket text. Desktop DPR is usually 1, so the cap never bit
    // there — which is why the same scene looked sharp on a 4K PC. Now every
    // tier at least reaches near-native density on phones; `low` stays a real
    // battery/perf saver, `high` reaches the device's true resolution.
    const dpr = window.devicePixelRatio || 1;
    const cap = this.quality === "low" ? 1.5 : this.quality === "high" ? 3 : 2.5;
    this.renderer.setPixelRatio(Math.min(dpr, cap));
  }

  private readonly animate = (): void => {
    if (this.destroyed || !this.renderer) return;
    const delta = Math.min(this.clock.getDelta(), 0.05);
    this.updateMovement(delta);
    this.updateNoren();
    this.updateProductHover(delta);
    this.camera.rotation.order = "YXZ";
    this.camera.rotation.y = this.yaw;
    this.camera.rotation.x = this.pitch;
    this.renderer.render(this.scene, this.camera);
    this.frame = requestAnimationFrame(this.animate);
  };

  private updateMovement(delta: number): void {
    this.updateProximityZoneHighlight();
    if (this.autoTarget && this.hoveredZone) this.clearHoveredZone();
    const keyboardX = Number(this.keys.has("KeyD") || this.keys.has("ArrowRight")) - Number(this.keys.has("KeyA") || this.keys.has("ArrowLeft")) + this.moveInput.x;
    const keyboardY = Number(this.keys.has("KeyW") || this.keys.has("ArrowUp")) - Number(this.keys.has("KeyS") || this.keys.has("ArrowDown")) + this.moveInput.y;
    if (keyboardX || keyboardY) {
      const forward = new THREE.Vector3(-Math.sin(this.yaw), 0, -Math.cos(this.yaw));
      const right = new THREE.Vector3(Math.cos(this.yaw), 0, -Math.sin(this.yaw));
      const movement = forward.multiplyScalar(keyboardY).add(right.multiplyScalar(keyboardX));
      if (movement.lengthSq() > 1) movement.normalize();
      this.tryMove(movement.multiplyScalar(WALK_SPEED * delta));
      return;
    }
    if (!this.autoTarget) return;
    const movement = this.autoTarget.clone().sub(this.camera.position);
    movement.y = 0;
    const remaining = movement.length();
    if (remaining < 0.08) {
      this.autoTarget = null;
      return;
    }
    movement.setLength(Math.min(WALK_SPEED * delta, remaining));
    if (!this.tryMove(movement)) this.autoTarget = null;
  }

  private tryMove(movement: THREE.Vector3): boolean {
    let moved = false;
    const nextX = this.camera.position.x + movement.x;
    const nextZ = this.camera.position.z + movement.z;
    if (this.canMoveTo(nextX, this.camera.position.z)) { this.camera.position.x = nextX; moved = true; }
    if (!this.ageConfirmed && this.camera.position.z >= AGE_THRESHOLD_Z && nextZ < AGE_THRESHOLD_Z) {
      this.autoTarget = null;
      if (!this.agePromptRequested) {
        this.agePromptRequested = true;
        this.onAgeConfirmed();
      }
      return moved;
    }
    if (this.canMoveTo(this.camera.position.x, nextZ)) { this.camera.position.z = nextZ; moved = true; }
    return moved;
  }

  private updateNoren(): void {
    // Keep the curtain closed behind the first age dialog, then resume the
    // existing proximity animation as soon as the visitor confirms.
    const proximity = this.ageConfirmed
      ? THREE.MathUtils.clamp(1 - Math.abs(this.camera.position.z - NOREN_Z) / 2.25, 0, 1)
      : 0;
    this.norenPanels.forEach((panel, index) => {
      panel.rotation.y = panel.userData.direction * proximity * 0.48;
      panel.rotation.z = Math.sin(performance.now() * 0.0014 + index) * 0.004;
      panel.position.x = panel.userData.baseX + panel.userData.direction * proximity * 0.34;
    });
  }

  private canMoveTo(x: number, z: number): boolean {
    if (x < STORE_BOUNDS.minX || x > STORE_BOUNDS.maxX || z < STORE_BOUNDS.minZ || z > STORE_BOUNDS.maxZ) return false;
    return !this.collisions.some((rect) => x + PLAYER_RADIUS > rect.minX && x - PLAYER_RADIUS < rect.maxX && z + PLAYER_RADIUS > rect.minZ && z - PLAYER_RADIUS < rect.maxZ);
  }
}
